import { Export } from "@phosphor-icons/react";
import clsx from "clsx";
import { type JSX, useState } from "react";
import { useTranslation } from "react-i18next";

import EmptyPlaceholder from "common/components/EmptyPlaceholder";
import ErrorPlaceholder from "common/components/ErrorPlaceholder";
import RenderIf from "common/components/RenderIf";
import RestrictedComponent from "common/components/RestrictedComponent";
import SearchInput from "common/components/SearchInput";
import { Button } from "common/components/ui/Button";
import Checkbox from "common/components/ui/Checkbox";

import { useCampaignContext } from "../../../../context/CampaignContext";
import useAudience from "../../../../datahooks/useAudience";
import useCancelledAudience from "../../../../datahooks/useCancelledAudience";
import useCampaignDetailsAudience from "../../../../hooks/useCampaignDetailsAudience";
import { exportProfilesToCSV } from "../../../../utils";
import SectionHeading from "../../SectionHeading";
import AddTarget from "./AddTarget";
import AudienceList from "./AudienceList";
import FiltersPopover from "./FiltersPopover";
import RemoveTargets from "./RemoveTargets";

import EmptyPlaceholderImage from "assets/images/empty-placeholders/no-profiles.png";

export default function CampaignAudience({
  openLeadPreview,
  isPreviewOpen,
}: {
  openLeadPreview: (leadId: string) => void;
  isPreviewOpen: boolean;
}) {
  const { t } = useTranslation("campaigns");
  const [selectedProfiles, setSelectedProfiles] = useState<string[]>([]);

  const { type } = useCampaignContext();

  const { audience, isLoadingAudience, isAudienceError, refetchAudience } =
    useAudience();
  const { cancelledAudience } = useCancelledAudience();

  const {
    filteredTargets,
    resetFilters,
    selectedStates,
    setSelectedStates,
    searchText,
    setSearchText,
    audienceWithCancellation,
  } = useCampaignDetailsAudience(audience, cancelledAudience);

  if (isAudienceError)
    return (
      <ErrorPlaceholder
        errorMessage="Error loading audience"
        onRetry={refetchAudience}
      />
    );

  function resetSelectedProfiles() {
    setSelectedProfiles([]);
  }

  const profilesToExport = selectedProfiles.length
    ? audience.filter(({ urn }) => selectedProfiles.includes(urn))
    : filteredTargets;

  let mainComponent: JSX.Element;

  if (isLoadingAudience || filteredTargets.length) {
    mainComponent = (
      <AudienceList
        audienceWithCancellation={
          isLoadingAudience ? new Array(12).fill({}) : filteredTargets
        }
        isLoading={isLoadingAudience}
        selectedProfiles={selectedProfiles}
        setSelectedProfiles={setSelectedProfiles}
        openLeadPreview={openLeadPreview}
        isPreviewOpen={isPreviewOpen}
      />
    );
  } else if (!audience.length) {
    mainComponent = (
      <EmptyPlaceholder
        imageSrc={EmptyPlaceholderImage}
        title="No targets found"
        subtitle="This campaign does not have any targets. You can add targets through integrations, via API, or manually."
      />
    );
  } else {
    mainComponent = (
      <EmptyPlaceholder
        imageSrc={EmptyPlaceholderImage}
        title="No targets found"
        subtitle={
          searchText
            ? `No targets found for “${searchText}”`
            : "No results found for the selected filters"
        }
        actionComponent={
          <Button onClick={resetFilters}>
            Reset {searchText ? "search" : "filters"}
          </Button>
        }
      />
    );
  }

  return (
    //without this wrapper the virtual list cuts off the bottom padding of the container
    <div className="flex flex-col gap-4">
      <SectionHeading
        title={t("campaignDetails.audienceTitle")}
        subtitle={t("campaignDetails.audienceSubtitle")}
      />

      <section
        className={clsx(
          "flex flex-col gap-4",
          isPreviewOpen ? "2xl:flex-row-reverse" : "lg:flex-row-reverse",
        )}
      >
        <div className="flex items-center gap-x-2">
          <RenderIf condition={!!selectedProfiles.length}>
            <Button
              variant="outline-black"
              intent="iconOnly"
              onClick={resetSelectedProfiles}
            >
              <div>
                <Checkbox isStyleOnly checked="indeterminate" />
              </div>
            </Button>
          </RenderIf>

          <RestrictedComponent visibleForPermissions={["campaigns_full"]}>
            {selectedProfiles.length ? (
              <RemoveTargets
                selectedProfiles={selectedProfiles}
                onSuccess={resetSelectedProfiles}
              />
            ) : (
              <RenderIf condition={type === "list"}>
                <AddTarget />
              </RenderIf>
            )}
          </RestrictedComponent>

          <Button
            leftIcon={<Export weight="fill" />}
            variant="secondary-purple"
            className={clsx(
              "grow-1",
              isPreviewOpen ? "2xl:grow-0" : "lg:grow-0",
              !filteredTargets.length &&
                "pointer-events-none bg-black-200 text-black-400",
            )}
            asChild
          >
            <a
              href={exportProfilesToCSV(profilesToExport)}
              download="LinkedInProfiles.csv"
            >
              <span className="truncate">
                Export{" "}
                {selectedProfiles.length
                  ? `${selectedProfiles.length} Target${selectedProfiles.length > 1 ? "s" : ""}`
                  : `audience (${filteredTargets.length})`}
              </span>
            </a>
          </Button>
        </div>
        <div
          className={clsx(
            "flex flex-1 items-center gap-x-2",
            isPreviewOpen ? "2xl:mr-auto" : "lg:mr-auto",
          )}
        >
          <SearchInput
            value={searchText}
            placeholder="Search targets"
            onChange={(e) => setSearchText(e.currentTarget.value)}
            className={clsx(
              "flex-1",
              isPreviewOpen ? "2xl:max-w-80" : "lg:max-w-80",
            )}
            onClear={() => setSearchText("")}
          />
          <FiltersPopover
            selectedStates={selectedStates}
            setSelectedStates={setSelectedStates}
            audienceWithCancellation={audienceWithCancellation}
          />
        </div>
      </section>

      {mainComponent}
    </div>
  );
}
